import { SHIPPING_ENUM } from '@/entities/rma';
import {
  CreateReturnShippingInfo,
  PreviewShipmentModal,
  CreateReturnShippingSignature,
  EditShipmentModal,
} from '@/widgets/shipping-label-form';
import {
  DatePickerInput,
  TextInput,
  ConfirmModal,
  LoadingSkeleton,
  CustomSelect,
} from '@/shared/ui/components';
import { auth } from '@/shared/lib';
import { Switch } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';
import { useCreateShippingLabel } from './useCreateShippingLabel';
import './createShippingLabel.scss';
import ShippingLabelDevices from '../shippingLabelDevices/shippingLabelDevices';
import ShippingLabelHeader from '../shippingLabelHeader/shippingLabelHeader';
import ShippingRate from '../shippingRate/shippingRate';

/**
 * Page for creating an RMA outbound shipping label. Drives useCreateShippingLabel with a Custom Dimensions default package and renders the missing-information, preview and edit-contact modals, the shipment date, package select whose length, width, height and unit inputs stay disabled unless custom dimensions are chosen, weight, hazardous-material switch, signature options, device picker and rate list.
 *
 * @returns The create shipping label page element.
 */
const CreateShippingLabel = () => {
  const DEFAULT_PACKAGE: any = {
    label: (
      <div className="label-dimensions">
        <p>{t`Custom Dimensions`}</p>
      </div>
    ),
    value: 'Custom Dimensions',
    parcelId: null,
    weight: null,
  };

  const {
    state,
    headerRef,
    isMissingData,
    DIMENSIONS_UNIT_OPTIONS,
    toggleMissingModal,
    onSubmitMissingModal,
    togglePreviewShipmentModal,
    toggleEditShipmentModal,
    updateShipment,
    onChangeShipmentDate,
    handlePackageChange,
    handleChange,
    onChangeOptionSignature,
    onChangeSignature,
    handleDevicesSelect,
  } = useCreateShippingLabel(DEFAULT_PACKAGE);

  const isSender = [SHIPPING_ENUM.SENDER, SHIPPING_ENUM.FIRST_EDIT_SENDER].includes(
    state.openEditShipmentModal,
  );

  const isCustomDimension = state.package?.value === 'Custom Dimensions';

  return (
    <>
      <ConfirmModal
        className="confirm-missing-modal"
        isOpen={state.isOpenMissingModal}
        leftButtonName={t`Cancel`}
        isBoldLeftBtn
        isFilledRightButton
        rightButtonName={t`Enter Now`}
        title={t`Missing information`}
        customContent={
          <div className="custom-content">
            {t`The following required information is missing:`}
            <ul aria-label={t`Missing information`}>
              {_.map(state.missingModalData, (item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {t`Please select “Enter Now” to review and fill in the missing information.`}
          </div>
        }
        isSmallSize
        onClickLeftButton={toggleMissingModal}
        onClickRightButton={onSubmitMissingModal}
      />
      <PreviewShipmentModal
        data={state}
        onCancel={togglePreviewShipmentModal}
        open={state.isOpenPreviewShipmentModal}
        width={900}
        selectedRate={state.selectedRate}
        title={t`Review shipping label`}
        isShippingLabel
      />
      <EditShipmentModal
        open={Boolean(state.openEditShipmentModal)}
        isSender={isSender}
        onCancel={toggleEditShipmentModal}
        data={isSender ? state.senderContact : state.recipientContact}
        isRmaShippingLabel
        title={isSender ? t`Edit sender information` : t`Edit recipient information`}
        listCountries={state.listCountries}
        listStatesCA={state.listStatesCA}
        listStatesUS={state.listStatesUS}
        updateData={updateShipment}
      />

      <div
        ref={headerRef}
        className={classNames('create-shipping-label-page', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
        })}
      >
        <ShippingLabelHeader
          isLoadedData={state.isLoadedData}
          disabled={isMissingData || !state.selectedDevices.length || !state.selectedRate?.id}
          handleCreateClick={togglePreviewShipmentModal}
        />

        <div className="create-shipping-label-page__body">
          <div className="body-top">
            <div className="body-top__left-container">
              <LoadingSkeleton isLoading={!state.isLoadedData}>
                <div className="box">
                  <div className="box__header">{t`Packet information`}</div>
                  <div className="box__body">
                    <div className="input-date package-information">
                      <div className="package-information__top-container">
                        <DatePickerInput
                          className="shipment-date"
                          name="shipmentDate"
                          label={t`Shipment date`}
                          placeholder={t`Select date`}
                          belowLimit={dayjs().startOf('day').valueOf()}
                          value={state.shipmentDate}
                          onChange={onChangeShipmentDate}
                        />
                        <CustomSelect
                          className="package-dropdown"
                          size="middle"
                          showSearch
                          allowClear={false}
                          title={t`Package`}
                          aria-label={t`Package`}
                          name="package"
                          options={state.shipmentParcels}
                          value={state.package}
                          onChange={handlePackageChange}
                        />
                        <div className="dimmensions-input-group">
                          <span className="dimmensions-input-group__title">{t`Dimensions`}</span>
                          <div className="dimmensions-input-group__input-container">
                            <TextInput
                              isNumberFormat
                              isKeyboardNumberMobile
                              decimalScale={1}
                              name="length"
                              placeholder={t`Length`}
                              value={state.length}
                              onChange={handleChange}
                              disabled={!isCustomDimension}
                            />
                            <TextInput
                              isNumberFormat
                              isKeyboardNumberMobile
                              decimalScale={1}
                              name="width"
                              placeholder={t`Width`}
                              value={state.width}
                              onChange={handleChange}
                              disabled={!isCustomDimension}
                            />
                            <TextInput
                              isNumberFormat
                              isKeyboardNumberMobile
                              decimalScale={1}
                              name="height"
                              placeholder={t`Height`}
                              value={state.height}
                              onChange={handleChange}
                              disabled={!isCustomDimension}
                            />
                            <CustomSelect
                              size="middle"
                              showSearch
                              allowClear={false}
                              style={{ minWidth: 100 }}
                              name="distanceUnit"
                              options={DIMENSIONS_UNIT_OPTIONS}
                              value={state.distanceUnit}
                              onChange={handleChange}
                              disabled={!isCustomDimension}
                              aria-label={t`Distance unit`}
                            />
                          </div>
                        </div>
                        <TextInput
                          isNumberFormat
                          isKeyboardNumberMobile
                          decimalScale={1}
                          name="weight"
                          label={`${t`Weight`} (lb)`}
                          placeholder={t`Weight`}
                          value={state.weight}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="package-information__bottom-container">
                        <div className="swich-checkbox">
                          <div className="swich-checkbox__header">{t`Hazardous materials`}</div>
                          <div className="swich-checkbox__checkbox">
                            <Switch
                              id="hazardous-materials-toggle"
                              {...({ width: 34, height: 18 } as any)}
                              checked={state.dangerousGoods}
                              onChange={() => handleChange('dangerousGoods', !state.dangerousGoods)}
                              aria-label={t`Contains hazardous materials`}
                            />
                            <div className="swich-checkbox__label">
                              {t`Contains hazardous materials`}
                            </div>
                          </div>
                        </div>
                        <CreateReturnShippingSignature
                          signatureConfirmation={state.signatureConfirmation}
                          onChangeOptionSignature={onChangeOptionSignature}
                          onChangeSignature={onChangeSignature}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </LoadingSkeleton>
            </div>
            <div className="body-top__right-container">
              <LoadingSkeleton isLoading={!state.isLoadedData}>
                <ShippingLabelDevices
                  notInLabelDevices={state.notInLabelDevices}
                  selectedDevices={state.selectedDevices}
                  handleDevicesSelect={handleDevicesSelect}
                />
              </LoadingSkeleton>
            </div>
          </div>
          <div className="body-bottom">
            <div className="body-bottom__left-container">
              <div className="shipping">
                <div className="shipping_sender">
                  <LoadingSkeleton isLoading={!state.isLoadedData}>
                    <CreateReturnShippingInfo
                      data={state.senderContact}
                      listStatesCA={state.listStatesCA}
                      listStatesUS={state.listStatesUS}
                      isSender
                      onEdit={() => {
                        toggleEditShipmentModal(SHIPPING_ENUM.SENDER);
                      }}
                    />
                  </LoadingSkeleton>
                  <div />
                </div>
                <div className="shipping_recipient">
                  <LoadingSkeleton isLoading={!state.isLoadedData}>
                    <CreateReturnShippingInfo
                      {...({ data: state.recipientContact, isHideEdit: true } as any)}
                    />
                  </LoadingSkeleton>
                </div>
              </div>
            </div>
            <div className="body-bottom__right-container">
              <LoadingSkeleton isLoading={!state.isLoadedData}>
                <ShippingRate
                  isLoadedData={state.isLoadedData}
                  isMissingData={isMissingData}
                  rates={state.rates}
                  selectedRate={state.selectedRate}
                  isLoadingRates={state.isLoadingRates}
                />
              </LoadingSkeleton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateShippingLabel;
